import { Box, Button, Switch, Typography, useMediaQuery } from "@mui/material";
import {
  DataGrid,
  gridClasses,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const PRIMARY = "#0B5C8A";

const primaryBtnSx = {
  height: 36,
  px: 2,
  gap: 1,
  borderRadius: "9px !important",
  background: `${PRIMARY} !important`,
  color: "#FFFFFF !important",
  border: "0 !important",
  fontFamily: FONT,
  fontSize: 13.5,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover": { background: "#084767 !important" },
};
const rowBtnSx = {
  minWidth: 0,
  height: 28,
  px: 1.25,
  borderRadius: "8px",
  border: "1px solid #D5DEE7",
  background: "#FFFFFF",
  color: INK,
  fontFamily: FONT,
  fontSize: 12.5,
  fontWeight: 600,
  textTransform: "none",
  "&:hover": { background: "#F4F7FA" },
};
const rolePillSx = {
  display: "inline-flex",
  alignItems: "center",
  px: 1.125,
  py: 0.25,
  borderRadius: "999px",
  background: "#EFEDFB",
  border: "1px solid #D3D0F2",
  color: "#4A3FBF",
  fontFamily: FONT,
  fontSize: 11.5,
  fontWeight: 700,
  lineHeight: 1.5,
  textTransform: "capitalize",
  whiteSpace: "nowrap",
};

const AllOrgsUsers = () => {
  const [users, setUsers] = useState([]);
  const orgId = JSON.parse(localStorage.getItem("org"));
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 750px)");

  useEffect(() => {
    const getOrg = async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/organization/get-by-admin?username=${storedUser.username}&orgId=${orgId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      if (
        !res.data.orgLogo &&
        !res.data.orgUsername &&
        !res.data.orgShortform
      ) {
        navigate(`/dashboard/org-details`);
      }
    };
    getOrg();
  }, []);

  useEffect(() => {
    const getOrgUsers = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user/all?orgId=${orgId}&username=${storedUser.username}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setUsers(res.data);
      } catch (error) {
        console.log(error.response.data.Exception || "Access denied");
      }
    };
    getOrgUsers();
  }, [orgId]);

  const columns = [
    {
      field: "orgUserId",
      headerName: "Org User ID",
      flex: 1,
      renderCell: (params) => (
        <span style={{ fontFamily: MONO, fontSize: 12.5, color: INK_2 }}>{params.value}</span>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      renderCell: (params) => (
        <span style={{ fontWeight: 700, color: INK }}>{params.value}</span>
      ),
    },
    {
      field: "mobile",
      headerName: "Mobile No.",
      flex: 1,
      renderCell: (params) => (
        <span style={{ fontFamily: MONO, fontSize: 12.5, color: INK_2 }}>
          {params.value
            ? params.row.countryCode != null
              ? params.row.countryCode.includes("+")
                ? `${params.row.countryCode}${params.value}`
                : `+${params.row.countryCode}${params.value}`
              : `+${params.value}`
            : "-"}
        </span>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      renderCell: (params) => (
        <span style={{ color: INK_2 }}>{params.value}</span>
      ),
    },
    {
      field: "userRoleName",
      headerName: "User Role Name",
      flex: 1,
      renderCell: (params) => (
        <Box component="span" sx={rolePillSx}>
          {params.row.userRoleName.replace("_", " ").toLowerCase()}
        </Box>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, height: "100%" }}>
          <Button
            onClick={() => navigate(`/dashboard/${params.row.orgUserId}`)}
            sx={rowBtnSx}
          >
            Edit
          </Button>
          <Button
            onClick={() => handleRemoveUser(params.row.mobile)}
            sx={{
              ...rowBtnSx,
              color: "#A6342A",
              borderColor: "#F2CFCA",
              "&:hover": { background: "#FBEDEB" },
            }}
          >
            Remove
          </Button>
        </Box>
      ),
    },
  ];

  const rows = users ? users : "";

  const handleEnableDisable = async (orgUserId, isEnable) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/org-user?orgUserId=${orgUserId}`,
        {
          isEnable: !isEnable,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handleRemoveUser = async (mobile) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/org-user/remove?username=${storedUser.username}&orgUser=${mobile}&orgId=${orgId}`,
        {},
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setUsers((prev) => prev.filter((u) => u.mobile !== mobile));
    } catch (error) {
      console.log(error);
    }
  };
  return (
    <div style={{ fontFamily: FONT, color: INK }}>
      <Box
        sx={{
          display: { xs: "block", sm: "flex" },
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          mb: 0.5,
        }}
      >
        <Typography
          variant="h4"
          my={1}
          fontWeight={700}
          sx={{ fontFamily: FONT, fontSize: { xs: 24, sm: 27 }, fontWeight: 800, letterSpacing: "-0.028em", color: INK, mb: 0 }}
        >
          List of Users
        </Typography>
        <Link to={`/dashboard/create-user`} style={{ textDecoration: "none" }}>
          <Button
            className="btn-contained"
            sx={{ ...primaryBtnSx, width: { xs: "100%", sm: "auto" }, height: { xs: 44, sm: 36 }, mb: { xs: 1.5, sm: 1 } }}
          >
            <PersonAddAltRoundedIcon sx={{ fontSize: 18 }} /> Add User
          </Button>
        </Link>
      </Box>
      <p style={{ margin: "0 0 16px", fontSize: 13.5, fontWeight: 500, color: "#4A5A6E" }}>
        Users you have invited but not joined yet can be seen{" "}
        <Link to="/dashboard/invites" style={{ color: PRIMARY, fontWeight: 700 }}>here</Link>
      </p>
      <div>
        {isMobile ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {users?.map((user) => (
              <div
                key={user.orgUserId}
                className="card"
                style={{
                  background: "#FFFFFF",
                  border: `1px solid ${LINE}`,
                  borderRadius: 14,
                  padding: "14px 14px 8px",
                  boxShadow: "none",
                  margin: 0,
                }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}
                >
                  <div style={{ minWidth: 0 }}>
                    <Typography
                      fontSize={"16px"}
                      color={INK}
                      fontWeight={"800"}
                      sx={{ fontFamily: FONT, letterSpacing: "-0.015em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {user.name}
                    </Typography>
                    <Typography
                      fontSize={"12px"}
                      color={MUTED}
                      fontWeight={"500"}
                      sx={{ fontFamily: MONO, mt: 0.25 }}
                    >
                      ID: {user.orgUserId}
                    </Typography>
                  </div>
                  <Box
                    component="div"
                    className="card-tag"
                    sx={{ ...rolePillSx, flexShrink: 0 }}
                  >
                    {user.userRoleName.replace("_", " ").toLowerCase()}
                  </Box>
                </div>
                <Typography
                  color={INK_2}
                  fontSize={"13px"}
                  fontWeight={500}
                  mt={1.5}
                  sx={{ display: "flex", alignItems: "center", gap: 1, fontFamily: MONO }}
                >
                  <CallIcon sx={{ fontSize: 16, color: MUTED }} />{" "}
                  {user.mobile ? `+${user.mobile}` : "-"}{" "}
                </Typography>
                <Typography
                  color={INK_2}
                  fontSize={"13px"}
                  fontWeight={500}
                  mt={0.75}
                  mb={1.25}
                  sx={{ display: "flex", alignItems: "center", gap: 1, fontFamily: FONT, minWidth: 0, wordBreak: "break-all" }}
                >
                  <EmailIcon sx={{ fontSize: 16, color: MUTED }} /> {user.email}
                </Typography>
                <hr style={{ border: 0, borderTop: "1px solid #F1F4F8", margin: "0 -14px 6px" }} />
                <div style={{ display: "flex", justifyContent: "end", gap: 8 }}>
                  <Button
                    onClick={() => navigate(`/dashboard/${user.orgUserId}`)}
                    sx={{
                      minWidth: 0,
                      width: 44,
                      height: 44,
                      borderRadius: "12px",
                      border: `1px solid ${LINE}`,
                      background: "#F4F7FA",
                      color: `${INK_2} !important`,
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </Button>
                  <Button
                    onClick={() => handleRemoveUser(user.mobile)}
                    sx={{
                      minWidth: 0,
                      width: 44,
                      height: 44,
                      borderRadius: "12px",
                      border: "1px solid #F2CFCA",
                      background: "#FBEDEB",
                      color: "#A6342A !important",
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                </div>
              </div>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              background: "#fff",
              border: `1px solid ${LINE}`,
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 20,
                  },
                },
              }}
              pageSizeOptions={[20]}
              disableRowSelectionOnClick
              disableColumnSelector
              disableDensitySelector
              slots={{ toolbar: CustomToolbar }}
              getRowId={(row) => row.orgUserId}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
              }
              rowHeight={50}
              columnHeaderHeight={40}
              sx={{
                position: "relative",
                border: "none",
                fontFamily: `${FONT} !important`,
                color: INK,

                "& .MuiDataGrid-columnHeaderTitle": {
                  fontFamily: FONT,
                  fontWeight: "700 !important",
                  fontSize: 10.5,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                  color: MUTED,
                },
                "& .MuiDataGrid-columnHeaders": {
                  background: "#F7F9FC !important",
                  color: MUTED,
                  fontSize: "14px",
                  borderRadius: 0,
                  borderTop: `1px solid ${LINE}`,
                  borderBottom: `1px solid ${LINE}`,
                },
                "& .MuiDataGrid-columnHeader, & .MuiDataGrid-filler": {
                  background: "#F7F9FC !important",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#F4F7FA",
                },
                "& .MuiDataGrid-cell": {
                  fontSize: "13.5px",
                  border: "none",
                  borderBottom: "1px solid #F1F4F8",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnSeparator": {
                  display: "none",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: `1px solid ${LINE}`,
                  fontFamily: MONO,
                  color: MUTED,
                },
                "& .MuiTablePagination-root, & .MuiTablePagination-displayedRows": {
                  fontFamily: MONO,
                  fontSize: 12,
                  color: MUTED,
                },
                "& .css-1ccsd5i-MuiButtonBase-root-MuiButton-root": {
                  color: "#000 !important",
                  border: "1px solid #B0B0B0",
                  borderRadius: "12px",
                  padding: 1,
                },
                "& .MuiInputBase-root-MuiInput-root::after": {
                  borderBottom: "none !important",
                },
                "& .MuiInputBase-root-MuiInput-root": {
                  borderBottom: "none !important",
                },
                [`& .${gridClasses.row}.even`]: {
                  backgroundColor: "#FFFFFF",
                  // '&:hover': {
                  //   backgroundColor: "#f6f6f6",
                  // },
                },
                [`& .${gridClasses.row}.odd`]: {
                  backgroundColor: "#FCFDFE",
                },
                [`& .${gridClasses.row}.even:hover, & .${gridClasses.row}.odd:hover`]: {
                  backgroundColor: "#F4F7FA",
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none !important",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none !important",
                },
                "& .css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
                  borderBottom: "none !important",
                },
              }}
            />
          </Box>
        )}
      </div>
    </div>
  );
};

export default AllOrgsUsers;

function CustomToolbar() {
  return (
    <GridToolbarContainer
      sx={{ display: "flex", justifyContent: "end", px: 1.75, py: 1.25, gap: 1 }}
    >
      <GridToolbarQuickFilter
        sx={{
          border: "1px solid #D5DEE7",
          borderRadius: "9px",
          padding: "3px 10px",
          background: "#FFFFFF",
          "&:hover": {
            backgroundColor: "#F4F7FA",
          },
          "&:focus-within": {
            borderColor: PRIMARY,
          },
          "& .MuiInputBase-root": {
            borderRadius: "9px",
            fontFamily: FONT,
            fontSize: 13.5,
          },
          "& .MuiInputBase-root::before, & .MuiInputBase-root::after": {
            display: "none",
          },
          "& .MuiSvgIcon-root": { color: MUTED, fontSize: 18 },
          width: 300,
        }}
      />
      {/* <GridToolbarFilterButton /> */}
    </GridToolbarContainer>
  );
}
